import { nanoid } from 'nanoid';
import {
  sortType,
  sortDirection,
  postType,
  filterType,
  condition,
  filterMetricOption,
  badgeType,
} from './constants';
import facebookPlaceholder from './images/facebook-placeholder.jpg';
import twitterPlaceholder from './images/twitter-placeholder.jpg';
import linkedinPlaceholder from './images/linkedin-placeholder.jpg';
import instagramPlaceholder from './images/instagram-placeholder.svg';
import youtubePlaceholder from './images/youtube-placeholder.svg';
import defaultPlaceholder from './images/default-placeholder.jpg';

const keywordFilter = (post, filter) =>
  post.message.toLowerCase().includes(filter?.value?.toLowerCase());

const mapConditionToFunc = {
  [condition.EQUAL]: (postValue, compareValue) => +postValue === +compareValue,
  [condition.GT]: (postValue, compareValue) => postValue >= compareValue,
  [condition.LT]: (postValue, compareValue) => postValue <= compareValue,
};

const mapMetricToProp = {
  [filterMetricOption.likes.value]: 'likes',
  [filterMetricOption.comments.value]: 'comments',
  [filterMetricOption.impressions.value]: 'impressions',
  [filterMetricOption.shares.value]: 'shares',
  [filterMetricOption.clicks.value]: 'clicks',
  [filterMetricOption.videos.value]: 'videos',
};

const metricFilter = (post, filter) => {
  const compareFunc = mapConditionToFunc[filter.condition];
  const prop = mapMetricToProp[filter.metric];
  const postValue = post[prop];
  return compareFunc(postValue, filter?.value);
};

const postTypeFilter = (post, filter) => {
  switch (filter?.value) {
    case postType.IMAGE:
      return !!post.picture;
    case postType.VIDEO:
      return !!post.videos;
    case postType.TEXT:
      return !!post.message;
    default:
      return false;
  }
};

const badgesFilter = post => {
  return post.badges?.length > 0;
};

const mapTypeToFilter = {
  [filterType.KEYWORD]: keywordFilter,
  [filterType.METRIC]: metricFilter,
  [filterType.POST_TYPE]: postTypeFilter,
  [filterType.ONLY_BADGES]: badgesFilter,
};

export const getFilteredPosts = (posts, filters) => {
  let filteredPosts = posts;

  filters.forEach(filter => {
    filteredPosts = filteredPosts?.filter(post => {
      const filterFunc = mapTypeToFilter[filter.type];
      return filterFunc(post, filter);
    });
  });
  return filteredPosts;
};

export const getHashtags = string => {
  const regex = /#\w+/g;
  const withoutHashtags = string?.replaceAll(regex, '');
  const hashtags = string?.match(regex);

  return [hashtags, withoutHashtags];
};

const mapTypeToProp = {
  [sortType.PERFORMANCE]: 'rank',
  [sortType.LIKES]: 'reactions',
  [sortType.COMMENTS]: 'comments',
  [sortType.CLICKS]: 'clicks',
  [sortType.SHARES]: 'shares',
  [sortType.IMPRESSIONS]: 'impressions',
  [sortType.VIDEO_VIEWS]: 'videos',
};

export const sortPosts = (posts, type, direction) => {
  const prop = mapTypeToProp[type];
  if (prop == null) {
    return posts;
  }
  const sortedPosts = posts
    .slice()
    .sort((a, b) =>
      direction === sortDirection.ASC ? b[prop] - a[prop] : a[prop] - b[prop]
    );

  return sortedPosts;
};

export const getSelectedTypes = (types, type) => {
  let result;
  if (type === postType.ALL) {
    return [];
  }

  if (types.includes(type)) {
    result = types?.filter(t => t !== type);
    return result.length === 0 ? [] : result;
  }

  result = [...types?.filter(t => t !== postType.ALL), type];

  return result?.length === 3 ? [] : result;
};

export const getMetricSettings = () => ({
  id: nanoid(),
  type: filterType.METRIC,
  metric: null,
  condition: 'equal',
  value: '',
});

const addBadgeByMetric = (posts, prop, badge) => {
  let maxIndex = 0;
  let maxValue = 0;
  const result = posts.slice();

  result.forEach((post, i) => {
    const value = post[prop];
    if (value > maxValue) {
      maxValue = value;
      maxIndex = i;
    }
  });

  result[maxIndex].badges = [...result[maxIndex].badges, badge];

  return result;
};

export const addBadgesForPosts = posts => {
  const performanceBadges = [
    badgeType.FIRST,
    badgeType.SECOND,
    badgeType.THIRD,
    badgeType.WORST_FIRST,
    badgeType.WORST_SECOND,
    badgeType.WORST_THIRD,
  ];
  if (posts?.length === 0) {
    return [];
  }
  let postsArr = posts.map(post => ({ ...post, badges: [] }));
  const postsArrWithSortedIdx = postsArr.map((post, idx) => [post, idx]);
  if (postsArrWithSortedIdx.length <= 6) {
    postsArr = postsArr.map((post, idx) => {
      return {
        ...post,
        badges: [performanceBadges[idx]],
      };
    });
  } else {
    postsArr[0].badges = [badgeType.FIRST];
    postsArr[1].badges = [badgeType.SECOND];
    postsArr[2].badges = [badgeType.THIRD];
    postsArr[postsArr.length - 3].badges = [badgeType.WORST_THIRD];
    postsArr[postsArr.length - 2].badges = [badgeType.WORST_SECOND];
    postsArr[postsArr.length - 1].badges = [badgeType.WORST_FIRST];
  }
  postsArr = addBadgeByMetric(postsArr, 'likes', badgeType.LIKES);
  postsArr = addBadgeByMetric(postsArr, 'comments', badgeType.COMMENTS);
  postsArr = addBadgeByMetric(postsArr, 'impressions', badgeType.IMPRESSIONS);

  return postsArr;
};

export const getSortTooltip = (type, direction) => {
  switch (type) {
    case sortType.PERFORMANCE:
      return direction === sortDirection.ASC ? 'Best first' : 'Worst first';
    case sortType.LIKES:
    case sortType.COMMENTS:
    case sortType.SHARES:
    case sortType.CLICKS:
      return direction === sortDirection.ASC ? 'Most first' : 'Least first';
    default:
      return direction === sortDirection.ASC ? 'Best first' : 'Worst first';
  }
};

const mapConditionToText = {
  [condition.GT]: 'more than',
  [condition.LT]: 'lower than',
  [condition.EQUAL]: 'exactly',
};

export const getTooltipForFilter = (type, metric, conditionType, value) => {
  const conditionText = mapConditionToText[conditionType];
  switch (type) {
    case filterType.POST_TYPE:
      return `Posts with ${value}`;
    case filterType.METRIC:
      return `Posts with ${conditionText} ${value} ${metric}`;
    case filterType.KEYWORD:
    case filterType.ONLY_BADGES:
    default:
      return value;
  }
};

export const getImagePlaceholder = platform => {
  switch (platform) {
    case 'facebook':
      return facebookPlaceholder;
    case 'twitter':
      return twitterPlaceholder;
    case 'linkedin':
      return linkedinPlaceholder;
    case 'instagram':
      return instagramPlaceholder;
    case 'youtube':
      return youtubePlaceholder;
    default:
      return defaultPlaceholder;
  }
};
